import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contractsApi, type UpdateContractBody } from '@/api/contracts';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { useToast } from '@/components/Toast';
import {
  buildDiffPayload,
  checkEditForm,
  isoToDate,
  type DraftEdit,
  type InitialContractHeader,
} from './contract-edit-validation';

// ContractEditModal — DRAFT-only header editor. Calls
// PATCH /contracts/:id (BR-CNT-06: DRAFT contracts are mutable;
// ACTIVE+ are immutable and require terminate-and-replace).
//
// Lines / pricings are NOT editable through this modal — the
// server doesn't accept them on PATCH. To revise lines, the
// operator deletes the DRAFT and creates a fresh one through
// ContractCreateWizard.

interface Props {
  open: boolean;
  onClose: () => void;
  contractId: string;
  contractHumanId: string;
  initial: InitialContractHeader;
}

export function ContractEditModal({ open, onClose, contractId, contractHumanId, initial }: Props) {
  const toast = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState<DraftEdit>(() => ({
    startDate: isoToDate(initial.startDate),
    endDate: isoToDate(initial.endDate),
    durationMonths: initial.durationMonths,
    renewalScheduledAt: isoToDate(initial.renewalScheduledAt),
  }));
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: (body: UpdateContractBody) => contractsApi.update(contractId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      qc.invalidateQueries({ queryKey: ['contracts', contractId] });
      toast.success(`${contractHumanId} 계약을 저장했습니다.`, '계약 편집');
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);

    const formError = checkEditForm(form);
    if (formError) {
      setError(formError);
      return;
    }

    const body = buildDiffPayload(initial, form);
    if (Object.keys(body).length === 0) {
      setError('변경 사항이 없습니다');
      return;
    }

    update.mutate(body);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${contractHumanId} 초안 편집`}
      width={460}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="contract-edit-form" disabled={update.isPending}>
            {update.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <form id="contract-edit-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <div style={info}>
          <Icon name="alert" size={14} color="var(--report-new)" style={{ marginTop: 1 }} />
          <span>
            초안 상태에서만 헤더를 고칠 수 있습니다. 라인이나 가격 책정을 바꾸려면 초안을 삭제하고
            새 계약을 만들어 주세요.
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="시작일" htmlFor="e-start" required>
            <Input
              id="e-start"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
            />
          </Field>
          <Field label="종료일" htmlFor="e-end" required>
            <Input
              id="e-end"
              type="date"
              value={form.endDate}
              onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
            />
          </Field>
        </div>

        <Field
          label="개월 수"
          htmlFor="e-months"
          hint="소수점 둘째 자리까지 적을 수 있습니다. 예: 12.00"
        >
          <Input
            id="e-months"
            inputMode="decimal"
            value={form.durationMonths}
            onChange={(e) =>
              setForm((f) => ({ ...f, durationMonths: e.target.value.replace(/[^0-9.]/g, '') }))
            }
            style={{ fontFamily: 'var(--font-mono)' }}
          />
        </Field>

        <Field
          label="재계약 알림일 (선택)"
          htmlFor="e-renewal"
          hint="비워 두면 종료일을 기준으로 자동 계산됩니다"
        >
          <Input
            id="e-renewal"
            type="date"
            value={form.renewalScheduledAt}
            onChange={(e) => setForm((f) => ({ ...f, renewalScheduledAt: e.target.value }))}
          />
        </Field>
      </form>
    </Modal>
  );
}

const info = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
  padding: 10,
  marginBottom: 14,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-info-subtle)',
  color: 'var(--fg-muted)',
  fontSize: 12,
} as const;
